import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { CITIES } from "@/data/site";
import { Reveal, SectionHead } from "./Reveal";
import { EASE } from "@/lib/scroll";

const slug = (n) => n.split(" ")[0].toLowerCase();

const RadiusMap = ({ active, setActive }) => (
  <div className="relative aspect-square w-full max-w-[540px] mx-auto">
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
      <circle cx="50" cy="50" r="46" fill="#EEF3E6" />
      {[46, 34, 22, 11].map((r, i) => (
        <motion.circle key={r} cx="50" cy="50" r={r} fill="none" stroke="#2F5D3A" strokeOpacity={i === 0 ? 0.5 : 0.15} strokeWidth={i === 0 ? 0.35 : 0.2} strokeDasharray={i === 0 ? "1 1.2" : "none"}
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2, ease: EASE, delay: i * 0.15 }} />
      ))}
      <circle cx="50" cy="50" r="46" fill="#9CC26A" fillOpacity="0.18" className="pulse-ring" />
      <line x1="50" y1="50" x2="96" y2="50" stroke="#2F5D3A" strokeWidth="0.3" />
      <text x="73" y="48" fill="#2F5D3A" fontSize="2.8" textAnchor="middle" fontWeight="600">ca. 30 km</text>
    </svg>
    {CITIES.map((c) => (
      <button key={c.name} data-testid={`area-city-dot-${slug(c.name)}`} onMouseEnter={() => setActive(c.name)} onFocus={() => setActive(c.name)}
        className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${c.x}%`, top: `${c.y}%` }}>
        <span className={`rounded-full transition-transform duration-300 ${c.main ? "w-4 h-4 bg-[#111511] ring-4 ring-[#9CC26A]" : "w-2.5 h-2.5 bg-[#2F5D3A]"} ${active === c.name ? "scale-150" : ""}`} />
        <span className={`mt-1.5 text-[11px] sm:text-xs font-medium whitespace-nowrap ${active === c.name || c.main ? "text-[#111511]" : "text-[#4A5049]"}`}>{c.name}</span>
      </button>
    ))}
  </div>
);

export const Area = () => {
  const [active, setActive] = useState("Bottrop");
  return (
    <section id="einsatzgebiet" data-testid="service-area-section" className="py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <SectionHead eyebrow="Einsatzgebiet" lines={["Gartenpflege in Bottrop", <span className="text-[#5E8C3A]">und Umgebung.</span>]}
          text="Unser Hauptgebiet ist Bottrop. Außerdem pflegen wir Gärten in den Nachbarstädten im Umkreis von rund 30 Kilometern – kurze Wege, schnelle Termine." />
        <div className="mt-14 lg:mt-16 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <ul className="flex flex-wrap gap-2" data-testid="area-city-list">
              {CITIES.map((c) => (
                <li key={c.name}>
                  <button onMouseEnter={() => setActive(c.name)} onClick={() => setActive(c.name)} data-testid={`area-city-chip-${slug(c.name)}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300 ${active === c.name ? "bg-[#111511] text-white border-[#111511]" : "bg-white border-[#111511]/10 text-[#30352F] hover:border-[#111511]/30"}`}>
                    <MapPin className="w-3.5 h-3.5" /> {c.name}
                  </button>
                </li>
              ))}
            </ul>
            <p data-testid="area-further-note" className="mt-6 rounded-2xl bg-[#EEF3E6] px-5 py-4 text-[15px] text-[#30352F]"><strong className="font-semibold text-[#111511]">Weiter als 30 km entfernt?</strong> Sprechen Sie uns gerne an – darüber reden wir.</p>
          </Reveal>
          <Reveal className="lg:col-span-7 order-1 lg:order-2" delay={0.1}>
            <RadiusMap active={active} setActive={setActive} />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
